import React, { useCallback, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import type H from '@here/maps-api-for-javascript';
import {
  CompoundOccupancy,
  CompoundSlot,
  CompoundSlotOccupancy,
  SimulatedVehicle,
} from '@/types/CompoundMap';
import {
  recordedReason,
  recordedStatus,
} from '@/modules/commandCentre/compound/RecordedVehicleDetails';
import {
  featureId,
  isMapped,
  slotLabel,
} from '@/modules/commandCentre/compound/spatial';
import '@/modules/commandCentre/compound/CompoundTooltip.scss';

type Props = {
  map: H.Map;
  feature?: CompoundSlot;
  pinned: boolean;
  mode: 'none' | 'recorded' | 'simulation';
  row?: CompoundSlotOccupancy;
  simulatedVehicle?: SimulatedVehicle;
  positionSource?: CompoundOccupancy['positionSource'];
  stale: boolean;
  onClose: () => void;
};
type Point = { x: number; y: number };
type MapPointer = { currentPointer?: { viewportX: number; viewportY: number } };
const finitePoint = (point?: Point | null): point is Point =>
  !!point && Number.isFinite(point.x) && Number.isFinite(point.y);
const coordinate = (value: number) =>
  Number.isFinite(value) ? value.toFixed(6) : '—';

/** Map-native events only position this card; they never rebuild React's slot snapshot. */
export function CompoundTooltip(props: Props) {
  const {
    map,
    feature,
    pinned,
    mode,
    row,
    simulatedVehicle,
    positionSource,
    stale,
    onClose,
  } = props;
  const mapElement = map.getElement?.();
  const host = mapElement?.parentElement;
  const card = useRef<HTMLDivElement | null>(null);
  const latest = useRef(props);
  latest.current = props;
  const schedule = useRef(() => {});
  const observer = useRef<ResizeObserver | null>(null);
  const setCard = useCallback((node: HTMLDivElement | null) => {
    if (card.current) observer.current?.unobserve(card.current);
    card.current = node;
    if (node) observer.current?.observe(node);
    schedule.current();
  }, []);

  useLayoutEffect(() => {
    if (!host || !mapElement) return;
    let frame = 0;
    let pointer: Point | undefined;
    let lastAnchor: Point | undefined;
    let lastFeature: string | undefined;
    const position = () => {
      frame = 0;
      const node = card.current;
      const current = latest.current;
      if (!node || !current.feature) return;
      const id = featureId(current.feature);
      if (id !== lastFeature) lastAnchor = undefined;
      lastFeature = id;
      const hostRect = host.getBoundingClientRect();
      const mapRect = mapElement.getBoundingClientRect();
      const offsetX =
        mapRect.left - hostRect.left - host.clientLeft + host.scrollLeft;
      const offsetY =
        mapRect.top - hostRect.top - host.clientTop + host.scrollTop;
      const left = Math.max(0, offsetX),
        top = Math.max(0, offsetY);
      const right = Math.min(host.clientWidth, offsetX + mapRect.width);
      const bottom = Math.min(host.clientHeight, offsetY + mapRect.height);
      const width = Math.max(0, right - left),
        height = Math.max(0, bottom - top);
      if (!width || !height) {
        node.style.visibility = 'hidden';
        return;
      }
      const marginX = Math.min(8, width / 4),
        marginY = Math.min(8, height / 4);
      node.style.maxWidth = `${width - marginX * 2}px`;
      node.style.maxHeight = `${height - marginY * 2}px`;
      // Keep the box itself inside very small split views, including its padding.
      node.style.padding = `${Math.min(13, height / 8)}px ${Math.min(15, width / 8)}px`;
      node.style.borderWidth = `${Math.min(1, width / 8, height / 8)}px`;
      let anchor = pointer;
      if (current.pinned) {
        const { latitude: lat, longitude: lng } = current.feature.properties;
        const projected =
          Number.isFinite(lat) && Number.isFinite(lng)
            ? map.geoToScreen?.({ lat, lng })
            : null;
        if (finitePoint(projected))
          lastAnchor = { x: projected.x, y: projected.y };
        anchor = lastAnchor ?? { x: mapRect.width / 2, y: mapRect.height / 2 };
      }
      if (!finitePoint(anchor)) {
        node.style.visibility = 'hidden';
        return;
      }
      node.style.left = `${Math.max(left + marginX, Math.min(offsetX + anchor.x + 15, right - marginX - node.offsetWidth))}px`;
      node.style.top = `${Math.max(top + marginY, Math.min(offsetY + anchor.y + 17, bottom - marginY - node.offsetHeight))}px`;
      node.style.visibility = 'visible';
    };
    const requestPosition = () => {
      if (!frame) frame = requestAnimationFrame(position);
    };
    schedule.current = requestPosition;
    const move = (event: MapPointer) => {
      pointer = event.currentPointer
        ? {
            x: event.currentPointer.viewportX,
            y: event.currentPointer.viewportY,
          }
        : undefined;
      requestPosition();
    };
    map.addEventListener('pointermove', move);
    map.addEventListener('mapviewchange', requestPosition);
    map.addEventListener('mapviewchangeend', requestPosition);
    window.addEventListener('resize', requestPosition);
    if (typeof ResizeObserver !== 'undefined') {
      observer.current = new ResizeObserver(requestPosition);
      observer.current.observe(host);
      observer.current.observe(mapElement);
      if (card.current) observer.current.observe(card.current);
    }
    requestPosition();
    return () => {
      cancelAnimationFrame(frame);
      schedule.current = () => {};
      observer.current?.disconnect();
      observer.current = null;
      map.removeEventListener('pointermove', move);
      map.removeEventListener('mapviewchange', requestPosition);
      map.removeEventListener('mapviewchangeend', requestPosition);
      window.removeEventListener('resize', requestPosition);
    };
  }, [host, map, mapElement]);
  useLayoutEffect(() => schedule.current());

  if (!host || !feature) return null;
  const vehicles =
    mode === 'recorded'
      ? (row?.vehicles ?? [])
      : mode === 'simulation' && simulatedVehicle
        ? [simulatedVehicle]
        : [];
  const status =
    mode === 'recorded'
      ? recordedStatus(row)
      : mode === 'simulation'
        ? simulatedVehicle
          ? 'Occupied · Simulated'
          : 'Empty · Simulation'
        : 'Occupancy not enabled';
  const swatch =
    mode === 'recorded'
      ? (row?.status ?? 'unknown')
      : simulatedVehicle && mode === 'simulation'
        ? 'occupied'
        : isMapped(feature)
          ? 'mapped'
          : 'unresolved';
  return createPortal(
    <div
      ref={setCard}
      className={`cc-compound-tooltip${pinned ? ' cc-compound-tooltip--pinned' : ''}`}
      role={pinned ? 'region' : 'tooltip'}
      aria-label={pinned ? 'Selected slot details' : undefined}
      style={{ visibility: 'hidden' }}
    >
      {pinned && (
        <button
          type="button"
          className="cc-compound-tooltip__close"
          aria-label="Close slot details"
          onClick={onClose}
        >
          ×
        </button>
      )}
      <strong className="cc-compound-tooltip__title">
        {slotLabel(feature)}
      </strong>
      <div className="cc-compound-tooltip__status">
        <i className={`cc-compound-tooltip__swatch ${swatch}`} />
        {status}
      </div>
      {mode === 'recorded' && recordedReason(row) && (
        <div className="cc-compound-tooltip__source">{recordedReason(row)}</div>
      )}
      {mode === 'recorded' && (
        <div className="cc-compound-tooltip__source">
          {positionSource === 'dev-test'
            ? 'DEV test positions · Artificially assigned'
            : 'Recorded locations'}
          {stale && <strong> · STALE — last known snapshot</strong>}
        </div>
      )}
      {vehicles.map((vehicle) => (
        <div key={'stockId' in vehicle ? vehicle.stockId : vehicle.id}>
          <div className="cc-compound-tooltip__vehicle">
            {vehicle.make || '—'} {vehicle.model || '—'}
          </div>
          <div className="cc-compound-tooltip__vin">
            VIN: {vehicle.vin || '—'}
          </div>
          <div className="cc-compound-tooltip__registration">
            {vehicle.registration || '—'}
          </div>
          <div className="cc-compound-tooltip__colour">
            {vehicle.colour || '—'}
          </div>
        </div>
      ))}
      <div className="cc-compound-tooltip__identity">
        Source identity {isMapped(feature) ? 'mapped' : 'unresolved'}
      </div>
      {feature.properties.drawingReference && (
        <div className="cc-compound-tooltip__drawing">
          Drawing reference, not linked
        </div>
      )}
      <div className="cc-compound-tooltip__position">
        {coordinate(feature.properties.latitude)},{' '}
        {coordinate(feature.properties.longitude)}
      </div>
    </div>,
    host,
  );
}
